import type { SpecificationDocument } from '../../../../../types';

export type WpsPreheatLimits = {
  minC: string;
  maxC: string;
  minNum: number | null;
  maxNum: number | null;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value) return null;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null;
    } catch {
      return null;
    }
  }
  return typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function readPreheatField(
  preheat: Record<string, unknown>,
  ...keys: string[]
): number | null {
  for (const key of keys) {
    const v = preheat[key];
    if (v == null || v === '') continue;
    const n = typeof v === 'number' ? v : parseFloat(String(v));
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function collectPreheatCandidates(details: Record<string, unknown>): Record<string, unknown>[] {
  const directKeys = [
    'preheatTemperature',
    'preheat_temperature',
    'preheat',
    'preHeat',
    'preheatTemp',
    'preheat_temp',
    'wpsPreheat',
    'wps_preheat',
  ];
  const candidates: Record<string, unknown>[] = [details];

  for (const key of directKeys) {
    const candidate = asRecord(details[key]);
    if (candidate) candidates.push(candidate);
  }

  for (const [key, value] of Object.entries(details)) {
    if (!key.toLowerCase().includes('preheat')) continue;
    const candidate = asRecord(value);
    if (candidate) candidates.push(candidate);
  }

  return candidates;
}

function collectWpsDetails(wps: SpecificationDocument): Record<string, unknown>[] {
  const out: Record<string, unknown>[] = [];
  const rootDetails = asRecord(wps.details);
  if (rootDetails) out.push(rootDetails);

  if (Array.isArray(wps.files)) {
    for (const file of wps.files) {
      const fileRecord = asRecord(file);
      const fileDetails = asRecord(fileRecord?.details);
      if (fileDetails) out.push(fileDetails);
    }
  }

  return out;
}

/** Reads min/max °C from the pipe WPS specification `details.preheatTemperature`. */
export function getWpsPreheatLimitsFromSpecifications(
  specifications: SpecificationDocument[] | undefined
): WpsPreheatLimits | null {
  if (!specifications?.length) return null;
  const wpsList = specifications.filter((s) => String(s.type).toUpperCase() === 'WPS');

  for (const wps of wpsList) {
    for (const details of collectWpsDetails(wps)) {
      for (const candidate of collectPreheatCandidates(details)) {
        const minNum = readPreheatField(
          candidate,
          'minC',
          'min_c',
          'minimum',
          'min',
          'minTemp',
          'minTemperature',
          'minimumTemperature',
          'preheatMin',
          'preheatMinC',
          'preheat_min',
          'preheat_min_c',
          'preheatTemperatureMin',
          'minimumPreheat',
          'minimumPreheatC',
        );
        const maxNum = readPreheatField(
          candidate,
          'maxC',
          'max_c',
          'maximum',
          'max',
          'maxTemp',
          'maxTemperature',
          'maximumTemperature',
          'preheatMax',
          'preheatMaxC',
          'preheat_max',
          'preheat_max_c',
          'preheatTemperatureMax',
          'maximumPreheat',
          'maximumPreheatC',
        );
        if (minNum == null && maxNum == null) continue;

        return {
          minC: minNum != null ? String(minNum) : '',
          maxC: maxNum != null ? String(maxNum) : '',
          minNum,
          maxNum,
        };
      }
    }
  }

  return null;
}

export function actualPreheatMeetsWpsLimits(
  actualNum: number | null,
  limits: WpsPreheatLimits | null
): 'pass' | 'fail' | 'neutral' {
  if (actualNum == null || !limits) return 'neutral';
  const { minNum, maxNum } = limits;
  if (minNum != null && actualNum < minNum) return 'fail';
  if (maxNum != null && actualNum > maxNum) return 'fail';
  if (minNum != null || maxNum != null) return 'pass';
  return 'neutral';
}
